import { toggleArray } from '@domain-group/javascript-utils'

import { Box, ToggleButton } from '@domain-group/suit-theme'

import useAgency from '@hooks/useAgency'
import usePortalOptionValues from '@hooks/usePortalOptionValues'

import handleCallbackAction from '@functions/handleCallbackAction'

import { AgencyModel, PortalModel, PortalOptionModel } from '@models/types'

type UpdateOptionsParams = {
  agencyOptions: {
    disabled_portal_option_values?: {
      [key: string]: number[],
    },
    enabled_portal_option_values?: {
      [key: string]: number[],
    },
  },
  portal: PortalModel,
  portalOptionValueId: number,
  updateAgencyFn: (agencyParams: AgencyModel) => void,
}

const updateEnabledOptions = (params: UpdateOptionsParams) => {
  const {
    agencyOptions, portal, portalOptionValueId, updateAgencyFn,
  } = params
  const { disabled_portal_option_values = {} } = agencyOptions || {}

  const disabledIdsForPortal: number[] = Array.isArray(disabled_portal_option_values?.[portal?.short_code])
    ? disabled_portal_option_values[portal?.short_code]
    : []

  const updatedIds = toggleArray(disabledIdsForPortal, portalOptionValueId)

  const updateAgencyEnabledOptionPayload = {
    callbacks: {
      action: updateAgencyFn,
    },
    entityParams: {
      options: {
        disabled_portal_option_values: { ...disabled_portal_option_values, [portal.short_code]: updatedIds },
      },
    },
    toastText: 'Agency Options Updated',
  }

  // @ts-expect-error TS2345
  return handleCallbackAction(updateAgencyEnabledOptionPayload)
}

const updateDefaultOptions = (params: UpdateOptionsParams) => {
  const {
    agencyOptions, portal, portalOptionValueId, updateAgencyFn,
  } = params

  const { enabled_portal_option_values = {} } = agencyOptions || {}

  const enabledIdsForPortal: number[] = Array.isArray(enabled_portal_option_values?.[portal?.short_code])
    ? enabled_portal_option_values[portal?.short_code]
    : []

  const updatedIds = toggleArray(enabledIdsForPortal, portalOptionValueId)

  const updateAgencyEnabledOptionPayload = {
    callbacks: {
      action: updateAgencyFn,
    },
    entityParams: {
      options: {
        enabled_portal_option_values: { ...enabled_portal_option_values, [portal.short_code]: updatedIds },
      },
    },
    toastText: 'Agency Default Options Updated',
  }

  // @ts-expect-error TS2345
  return handleCallbackAction(updateAgencyEnabledOptionPayload)
}

interface PortalOptionsProps {
  portal: PortalModel,
  portalOption: PortalOptionModel,
  selectedAgencyId: number,
}

const PortalOptions = (props: PortalOptionsProps) => {
  const { portal, portalOption, selectedAgencyId } = props

  const { filteredPortalOptionValues } = usePortalOptionValues({ filters: { portal_option_id: portalOption.id } })

  const { agency, callbacks: { updateAgency: updateAgencyFn } } = useAgency({ id: selectedAgencyId })

  const agencyOptions = agency?.options || {}
  const portalShortCode = portal?.short_code || ''
  // @ts-expect-error
  const disabledPortalOptionValues = agencyOptions?.disabled_portal_option_values?.[portalShortCode] ?? []
  // @ts-expect-error
  const enabledPortalOptionValues = agencyOptions?.enabled_portal_option_values?.[portalShortCode] ?? []

  return (
    <Box flexDirection="column">
      {filteredPortalOptionValues.map(portalOptionValue => (
        <Box
          key={portalOptionValue.id}
          width="100%"
          marginBottom="medium"
        >
          <Box
            flex="1"
            fontSize="xsmall"
            alignItems="center"
          >
            {portalOptionValue.title}
          </Box>

          <Box
            width="70px"
            alignItems="center"
          >
            <ToggleButton
              onClick={() => updateEnabledOptions({
                agencyOptions, portal, portalOptionValueId: portalOptionValue.id, updateAgencyFn,
              })}
              toggleOn={!disabledPortalOptionValues.includes(portalOptionValue.id)}
            />
          </Box>

          <Box
            width="70px"
            alignItems="center"
          >
            <ToggleButton
              // @ts-expect-error TS2322
              disabled={disabledPortalOptionValues.includes(portalOptionValue.id)}
              onClick={() => updateDefaultOptions({
                agencyOptions,
                portal,
                portalOptionValueId: portalOptionValue.id,
                updateAgencyFn,
              })}
              toggleOn={enabledPortalOptionValues.includes(portalOptionValue.id)}
            />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default PortalOptions
